import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const UserDetailModal = ({ show, onHide, user }) => {
  const [tabValue, setTabValue] = useState(0);

  if (!user) return null;

  // Normalize impossible travel details to array
  const impossibleTravelDetails = user.impossible_travel_details
    ? (Array.isArray(user.impossible_travel_details)
        ? user.impossible_travel_details
        : [user.impossible_travel_details])
    : [];

  const getClassificationChip = (classification) => {
    const config = {
      'HIGH_RISK': { color: 'error', label: 'High Risk' },
      'MEDIUM_RISK': { color: 'warning', label: 'Medium Risk' },
      'LOW_RISK': { color: 'info', label: 'Low Risk' },
      'NO_THREAT': { color: 'success', label: 'No Threat' }
    };
    const c = config[classification] || { color: 'default', label: 'Unknown' };
    return <Chip label={c.label} color={c.color} size="small" />;
  };

  const getSuspicionColor = (score) => {
    if (score >= 80) return 'error';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'info';
    return 'success';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  // Build dynamic tabs
  const tabs = [{ key: 'overview', label: 'Overview' }];
  if (impossibleTravelDetails.length > 0) {
    tabs.push({
      key: 'travel',
      label: `Impossible Travel (${impossibleTravelDetails.length})`,
    });
  }
  if (user.recent_signins && user.recent_signins.length > 0) {
    tabs.push({
      key: 'signins',
      label: `Recent Signins (${user.recent_signins.length})`,
    });
  }

  const activeTabKey = tabs[tabValue]?.key || 'overview';

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Dialog open={show} onClose={onHide} maxWidth="md" fullWidth>
      <DialogTitle>Suspicious Activity Detail</DialogTitle>

      <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* User Info Section */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {user.display_name || user.name || 'Unknown User'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {user.user_email || user.email || 'No email'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Chip
                label={`Suspicion Score: ${user.suspicion_score}/100`}
                color={getSuspicionColor(user.suspicion_score)}
                size="small"
                sx={{ mb: 1 }}
              />
              <br />
              {getClassificationChip(user.classification)}
            </Box>
          </Box>

          {/* Quick Stats */}
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              mb: 3,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                {user.confidence_score
                  ? `${(user.confidence_score * 100).toFixed(0)}%`
                  : user.confidence
                  ? `${(user.confidence * 100).toFixed(0)}%`
                  : 'N/A'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Confidence
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h6" sx={{ color: 'warning.main' }}>
                {user.indicators?.length ||
                  user.threat_indicators?.length ||
                  0}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Indicators
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h6" sx={{ color: 'error.main' }}>
                {user.impossible_travel_detected ? 1 : 0}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Impossible Travel
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h6" sx={{ color: 'info.main' }}>
                {user.recommendations?.length || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Actions
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          {tabs.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>

        {/* Overview Tab */}
        {activeTabKey === 'overview' && (
          <Box>
            {/* AI Analysis */}
            {user.reasoning && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>AI Analysis</AlertTitle>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-line' }}
                >
                  {user.reasoning}
                </Typography>
                {(user.confidence_score || user.confidence) && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mt: 1 }}
                  >
                    Confidence:{' '}
                    {(
                      (user.confidence_score || user.confidence) * 100
                    ).toFixed(0)}
                    % | Model: {user.model_used || 'Claude Haiku'}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Threat Indicators */}
            {((user.indicators && user.indicators.length > 0) ||
              (user.threat_indicators &&
                user.threat_indicators.length > 0)) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Threat Indicators
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(user.indicators || user.threat_indicators).map(
                    (indicator, idx) => (
                      <Chip key={idx} label={indicator} color="warning" size="small" />
                    )
                  )}
                </Box>
              </Box>
            )}

            {/* Recommendations */}
            {user.recommendations && user.recommendations.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Recommended Actions
                </Typography>
                <List dense disablePadding>
                  {user.recommendations.map((rec, idx) => (
                    <ListItem key={idx} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <ListItemText primary={rec} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Analysis Metadata */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Analyzed: {formatDate(user.created_at || user.analyzed_at)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Impossible Travel Tab */}
        {activeTabKey === 'travel' && (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Impossible Travel Detected</AlertTitle>
              <Typography variant="body2">
                The following signin patterns indicate physically impossible
                travel between locations.
              </Typography>
            </Alert>

            {impossibleTravelDetails.map((travel, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: 2,
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">
                    Incident #{idx + 1}
                  </Typography>
                  <Chip
                    label={`${travel.speed_kmh?.toFixed(0) || 'N/A'} km/h`}
                    color="error"
                    size="small"
                  />
                </Box>

                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 'bold', width: 120, borderBottom: 0 }}
                      >
                        From:
                      </TableCell>
                      <TableCell sx={{ borderBottom: 0 }}>
                        {travel.signin1_city || travel.from_city},{' '}
                        {travel.signin1_country || travel.from_country}
                        <br />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {formatDate(travel.signin1_time || travel.from_time)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', borderBottom: 0 }}>
                        To:
                      </TableCell>
                      <TableCell sx={{ borderBottom: 0 }}>
                        {travel.signin2_city || travel.to_city},{' '}
                        {travel.signin2_country || travel.to_country}
                        <br />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {formatDate(travel.signin2_time || travel.to_time)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', borderBottom: 0 }}>
                        Distance:
                      </TableCell>
                      <TableCell sx={{ borderBottom: 0 }}>
                        {travel.distance_km?.toFixed(0) || 'N/A'} km
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', borderBottom: 0 }}>
                        Time Elapsed:
                      </TableCell>
                      <TableCell sx={{ borderBottom: 0 }}>
                        {travel.time_diff_hours?.toFixed(2) || 'N/A'} hours
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {travel.assessment && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      <strong>Assessment:</strong> {travel.assessment}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Recent Signins Tab */}
        {activeTabKey === 'signins' && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mb: 2 }}
            >
              Most recent signin events used for analysis
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {user.recent_signins.map((signin, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(
                          signin.signin_timestamp || signin.created_at
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {signin.city}, {signin.country}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {signin.ip_address}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          (signin.success !== undefined
                            ? signin.success
                            : signin.is_success)
                            ? 'Success'
                            : 'Failed'
                        }
                        color={
                          (signin.success !== undefined
                            ? signin.success
                            : signin.is_success)
                            ? 'success'
                            : 'error'
                        }
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', mr: 'auto' }}
        >
          User ID: {user.user_id}
        </Typography>
        <Button onClick={onHide}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailModal;
